import { useEffect, useRef, useState, memo, useContext, Fragment } from 'react'
import Input from '../input/Input'
import Checkbox from '../input/Checkbox'
import Select from '../select/Select'
import PlanSelect from '../select/PlanSelect'
import PlanSelectPopup from '../planSelectPopup/PlanSelectPopup'
import InputRange from '../input/InputRange'
import './registerChildCard.css'
import DatePicker from '../datePicker/DatePicker'
import { dateToAge } from '../../service/dateUtil'
import { familyMonthlyDonation, familyGrant, familyTierAvailable, familyChildrenOptions, FAMILY_MORE_CHILDREN, FAMILY_MIN_LOAN, FAMILY_MAX_LOAN, FAMILY_STEP } from '../../service/familyPlanUtil'
import { appContext } from '../../service/AppContext'
import { registrationContex } from '../../service/RegisrationContext'
import { kidAgeMonths, kidSelectedRoutes, rowMaxUnits, getAvailablePlans, kidTotalUnits, TOTAL_UNITS_PER_CHILD } from '../../service/planUtils'


const RegisterChildCard = memo(({ index = 0, id, name, relationshipType, relationshipOther, idType, personId, passportId, birthday, age, preBorn, plan, units, loanAmount, plans = [], updateCB, removeKid, addPlanRow, removePlanRow, personIdValidation, removeDisabled = false, familyMode = false, familySubMode }) => {

  const { plans: allPlans } = useContext(appContext)
  const registration = useContext(registrationContex)
  const { familyPlans, familyChildrenCount, selectFamilyChildrenCount } = registration || {}

  // Family "yet to have children" hides all child identity fields (acts like preborn)
  const familyPreBorn = familyMode && familySubMode === 'preBorn';
  const familyHasChildren = familyMode && familySubMode === 'hasChildren';

  // The children count picks the plan tier; "more than 3" has no tier at all
  const familyTooManyChildren = familyHasChildren && familyChildrenCount === FAMILY_MORE_CHILDREN;
  const familyCountSelected = familyHasChildren && !!familyChildrenCount && !familyTooManyChildren;
  // No tier fits this family (too many children, or the oldest child is above every tier's age)
  const familyNotEligible = familyHasChildren &&
    (familyTooManyChildren || (familyCountSelected && age != null && !familyTierAvailable(familyPlans, familyChildrenCount, age)));

  const showPlanSelect = familyMode
    ? (familyPreBorn
      ? true
      : (familyCountSelected && !!name && age !== null && !!birthday &&
        ((idType === 'Person_Id' && personId) || (idType === 'Passport_Id' && passportId))))
    : ((preBorn || !!name) && age !== null &&
      ((relationshipType && relationshipType !== 'Other') || (relationshipType === 'Other' && relationshipOther)) &&
      (preBorn || ((idType === 'Person_Id' && personId) || (idType === 'Passport_Id' && passportId))));

  // Standard multi-plan derived state (DB driven availability)
  // Plan selection is age-driven only - identity fields (name/kinship/id) are NOT required to choose/add plans
  const canSelectPlan = age !== null;
  const kid = { id, name, age, plans, preBorn, birthday };
  const ageMonths = kidAgeMonths(kid);
  const availableCount = getAvailablePlans(allPlans, ageMonths).length;
  const canAddPlan = kidTotalUnits(kid) < TOTAL_UNITS_PER_CHILD && getAvailablePlans(allPlans, ageMonths, kidSelectedRoutes(kid)).length > 0;
  const hasPlan = familyMode ? !!plan : plans.some(row => row.plan);

  const planTitleWithPrefix = (p) => (!familyMode && p && age >= 1 && age <= 3) ? 'מרכזית - ' + p?.title : p?.title;

  const [minimize, setMinimze] = useState(false)
  const [openPopup, setOpenPopup] = useState(false) //Family popup state
  const [popupRowId, setPopupRowId] = useState(null) //Standard popup state - the plan row being edited

  const cardRef = useRef(true)
  const inputRef = useRef()

  const handleMinimize = (e) => {
    setMinimze(prev => !prev)
  }

  useEffect(() => {
    if (index)
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    setTimeout(() => {
      inputRef.current?.focus();
    }, 400);
  }, [])

  // Open / close the standard popup based on the update signal (multiple available plans => auto open)
  const handlePlanSignal = (signal) => {
    if (signal?.openPopup)
      setPopupRowId(signal.openPopupRowId)
    else
      setPopupRowId(null)
  }

  const handleUpdateAge = (e) => {
    if (typeof (updateCB) !== 'function') return
    const signal = updateCB(id, 'age')(e);
    if (!familyMode) handlePlanSignal(signal)
  }

  const handleUpdateBirthday = (e) => {
    if (typeof (updateCB) !== 'function') return
    const signal = updateCB(id, 'birthday')(e);
    if (familyMode) {
      // Auto open the plan popup as soon as a tier fits the new age, close it when none does
      if (familyTierAvailable(familyPlans, familyChildrenCount, dateToAge(e)))
        setOpenPopup(true)
      else if (openPopup)
        setOpenPopup(false)
    } else {
      handlePlanSignal(signal)
    }
  }

  const handleUpdatePreBorn = (value) => {
    if (familyMode && value)
      setOpenPopup(true)
    if (typeof (updateCB) === 'function') {
      const signal = updateCB(id, 'preBorn')(value)
      if (!familyMode) handlePlanSignal(signal)
    }
  }

  const handleAddPlanRow = () => {
    if (typeof (addPlanRow) === 'function') {
      const rowId = addPlanRow(id);
      setPopupRowId(rowId);
    }
  }

  // Standard mode unit options (40000 per unit). Family mode uses the loan slider below instead.
  const unitOptionsForMax = (max) => [...Array(max).keys()].map(i => ({ value: (i + 1).toString(), label: ((i + 1) * 40000).toLocaleString('he-IL') }));

  // Family loan-amount driven fields
  const familyLoan = Number(loanAmount) || 0;
  const familyMonthly = (familyMode && plan) ? familyMonthlyDonation(plan, familyLoan) : 0;
  const familyGrantValue = familyMode ? familyGrant(familyLoan) : 0;
  const familyLoanLabel = plan?.loanLabel || 'סך הלוואה';

  return (
    <div ref={cardRef} className="register-child-card" >
      {(!familyMode) &&
        <>
          <div className="title-wrapper">
            <div>
              <span className="child-index">{index + 1}.</span>
              {(name) ? <span className="child-title">{name}</span> : <span className="child-title gray-title">שם הילד</span>}
            </div>
            <img className={`card-minimize ${(minimize) && 'minimized'}`} src="./icons/chevron-up.svg" alt='הקטנת כרטיס' onClick={handleMinimize} />
          </div>
          <hr className="card-hr-desktop" />
        </>
      }

      {(!familyPreBorn) &&
      <div className="card-content-wrapper">
        <div className="fields-flex" >
          {/* The children count picks the plan tier - the oldest child's details follow only once it fits a tier */}
          {(familyHasChildren) &&
            <Select label='מספר הילדים שיש לך' className="family-children-count" name='family-children-count' value={familyChildrenCount || ''} options={familyChildrenOptions} onChange={selectFamilyChildrenCount} required>
              <option value="" disabled hidden>בחרו מספר ילדים</option>
            </Select>
          }
          {(familyHasChildren && !familyCountSelected) ? null :
          <Input myRef={inputRef} label={familyMode ? "שם הילד/ה הבכור/ה" : "שם הילד/ה"} className="child-name" name="child-name" placeholder="שם פרטי של הילד/ה" value={name} onChange={updateCB(id, 'name')} required={!preBorn} disabled={preBorn} />
          }
          {(!familyMode) &&
            <Select label='קרבה' className="relationship" name='relationship-type' value={relationshipType} onChange={updateCB(id, 'relationshipType')} required>
              <option value="Son/Daughter">בן/בת</option>
              <option value="Grandchild">נכד/ה</option>
              <option value="Brother/Sister">אח/ות</option>
              <option value="Nephew/Niece">אחיין/ית</option>
              <option value="Other">אחר</option>
            </Select>
          }
          {(!familyMode && relationshipType === 'Other') &&
            <Input type='text' label="פרט קרבה" className="relationship" name="relationship-other" value={relationshipOther} onChange={updateCB(id, 'relationshipOther')} required />
          }
        </div>
        {(familyHasChildren && !familyCountSelected) ? null :
        <div className="fields-flex" >
          <Select label='אמצעי זיהוי' className="id-type" name='id-type' value={(preBorn) ? ' ' : idType} onChange={updateCB(id, 'idType')}  locked={preBorn}>
            <option value="Person_Id">תעודת זהות</option>
            <option value="Passport_Id">דרכון</option>
          </Select>
          {(idType === 'Person_Id') && <Input type='text' label="תעודת זהות" className="child-person-id" name="child-person-id" value={(preBorn) ? ' ' : personId} onChange={updateCB(id, 'personId')} maxlength="9" invalid={personIdValidation?.invalid} errorText={personIdValidation?.errorText} required={!preBorn} disabled={preBorn} />}
          {(idType === 'Passport_Id') && <Input type='text' label="דרכון" className="child-person-id" name="passport-id" value={(preBorn) ? ' ' : passportId} onChange={updateCB(id, 'passportId')} required={!preBorn} disabled={preBorn} />}
          <div className='birthday-wrapper'>
            <DatePicker type={(preBorn) ? 'text' : 'date'} label="תאריך לידה" className="child-birthday" name="child-birthday" value={(preBorn) ? 'הילוד עדיין לא נולד' : birthday} onChange={handleUpdateBirthday} disabled={preBorn} max={new Date().toJSON().slice(0, 10)} required={!preBorn}>
              {(!familyMode) &&
                <Checkbox type='checkbox' label="טרום לידה -הילוד עדיין לא נולד" className="child-preBorn" name="child-preBorn" checked={preBorn} onChange={handleUpdatePreBorn} />
              }
            </DatePicker>
          </div>
        </div>
        }
      </div>
      }
      <hr className={`card-hr-mobile`} style={(hasPlan) ? {} : { display: 'none' }} />

      {familyMode ?
        <>
          {(openPopup) && <PlanSelectPopup closePopup={() => setOpenPopup(false)} setPlan={updateCB(id, 'plan')} name={name} age={age} preBorn={preBorn} setAge={updateCB(id, 'age')} familyPlan={true} />}
          {/* "more than 3 children" has no tier at all - the card shows only the block message */}
          {(!familyTooManyChildren) &&
            <div className={`fields-flex card-buttom ${(showPlanSelect) ? '' : 'desktop-or-wide'}`}  >
              <PlanSelect className={`child-plan ${(showPlanSelect) ? '' : 'desktop-or-wide'}`} label="מסלול" tooltip="בחרו את המסלול המתאים בתוכנית המשפחתית." value={planTitleWithPrefix(plan)} setOpenPopup={setOpenPopup} disabled={familyPreBorn ? false : (familyNotEligible || !familyCountSelected || age == null || dateToAge(birthday) > 16)} required={false}
                errorText="יש לבחור מסלול"
              />
            </div>
          }
          {(!!plan) &&
            <div className="family-loan-fields">
              <div className="family-loan-slider">
                <div className="family-loan-header">
                  <label htmlFor={`family-loan-${id}`}>{familyLoanLabel}</label>
                  <span className="family-loan-amount">₪{familyLoan.toLocaleString('he-IL')}</span>
                </div>
                <InputRange name="family-loan" id={`family-loan-${id}`} value={familyLoan || FAMILY_MIN_LOAN} onChange={updateCB(id, 'loanAmount')} min={FAMILY_MIN_LOAN} max={FAMILY_MAX_LOAN} step={FAMILY_STEP} />
              </div>
              <div className="family-loan-readonly fields-flex">
                <Input className="child-value" label="סכום התרומה החודשית"  value={'₪' + familyMonthly.toLocaleString('he-IL')} readonly tabIndex={-1} />
                <Input className="child-value" label="מענק לכל ילד"  value={'₪' + familyGrantValue.toLocaleString('he-IL')} readonly tabIndex={-1} />
              </div>
            </div>
          }
        </>
        :
        <>
          {(popupRowId) && <PlanSelectPopup closePopup={() => setPopupRowId(null)} setPlan={updateCB(id, 'rowPlan', popupRowId)} name={name} age={age} preBorn={preBorn} setAge={handleUpdateAge}
            ageMonths={ageMonths} excludeRoutes={kidSelectedRoutes(kid, popupRowId)} />}
          {plans.map((row, rowIndex) => {
            const maxUnits = rowMaxUnits(kid, row.rowId, row.plan);
            return (
              <Fragment key={row.rowId}>
                {(rowIndex > 0) && <hr className="card-hr-mobile" />}
                {(plans.length > 1) &&
                  <button className="remove-item remove-plan-row align-center" onClick={() => removePlanRow(id, row.rowId)}><img src="./icons/x-mark.svg" alt='הסר' />הסרת מסלול</button>}
                <div className={`fields-flex card-buttom plan-row ${(canSelectPlan || row.plan) ? '' : 'desktop-or-wide'}`} >
                  <PlanSelect className={`child-plan ${(canSelectPlan || row.plan) ? '' : 'desktop-or-wide'}`} label={(plans.length > 1) ? `מסלול ${rowIndex + 1}` : 'מסלול'} tooltip="לכל גיל מסלול שונה. אנא בחרו את המסלול המתאים לכם." value={planTitleWithPrefix(row.plan)} setOpenPopup={() => setPopupRowId(row.rowId)} disabled={(age == null || availableCount === 0)} required={false}
                    errorText={(!age) ? "יש לבחור תאריך לידה" :
                      (availableCount === 0) ? "אין מסלולים לגיל זה" :
                        "יש לבחור מסלול"}
                    OnValueButtonLabel={(age > 3) && 'פרטים'}
                  />

                  <Select className={`child-units ${(!!row.plan) ? '' : 'desktop-or-wide'}`} name="child-units" label='יחידות תרומה' value={row.units} onChange={updateCB(id, 'rowUnits', row.rowId)} options={[...Array(maxUnits).keys()].map(i => i + 1)} required>
                    <option value="0" disabled hidden>בחר כמות יחידות</option>

                  </Select>
                  <Select className={`child-value ${(!!row.plan) ? '' : 'desktop-or-wide'}`} name="child-value" label='סך כל ההלוואה' value={row.units} onChange={updateCB(id, 'rowUnits', row.rowId)} options={unitOptionsForMax(maxUnits)} >
                    <option value="0" disabled hidden>בחר סכום</option>

                  </Select>
                </div>
              </Fragment>
            )
          })}
          {(canAddPlan) &&
            <button className="add-plan-row" onClick={handleAddPlanRow}><img src="./icons/plus-circle-blue.svg" alt='+' />הוספת מסלול</button>}
        </>
      }
      {(!familyMode) &&
        <button className="remove-item align-center" onClick={(e) => { removeKid(id) }} disabled={removeDisabled}><img src="./icons/x-mark.svg" alt='הסר' />הסרה מרשימה</button>
      }
    </div>
  )
})
export default RegisterChildCard
